<template>
  <div class="app-page game-room">
    <!-- ====== Waiting State ====== -->
    <template v-if="!gameStarted">
      <div class="waiting-room">
        <div class="waiting-header">
          <h2>{{ gameTypeName }}房间</h2>
          <span class="room-code-label">房间码</span>
          <div class="room-code">{{ roomInfo.roomCode || '------' }}</div>
          <p class="waiting-hint">将房间码分享给好友，或复制链接邀请</p>
          <div class="waiting-actions-row">
            <button class="copy-btn" @click="copyRoomCode">复制房间码</button>
            <button class="rules-btn" @click="showRules = true">📖 玩法介绍</button>
          </div>
        </div>

        <div class="player-grid">
          <div
            v-for="(player, idx) in roomInfo.players || []"
            :key="player.userId + '-' + idx"
            class="player-slot"
            :class="{ 'is-host': player.userId === roomInfo.hostUserId, 'is-ai': player.isAi, 'is-self': player.userId === userId }"
          >
            <div class="slot-avatar">
              <img :src="player.avatar || defaultAvatar" alt="">
              <span class="online-indicator" :class="{ online: player.online }"></span>
            </div>
            <div class="slot-name">{{ player.isAi ? '🤖 ' + player.nickname : player.nickname }}</div>
            <div class="slot-badges">
              <span v-if="player.userId === roomInfo.hostUserId" class="host-badge">房主</span>
              <span v-if="player.isAi" class="ai-badge">人机</span>
              <span v-if="player.userId === userId" class="self-badge">我</span>
            </div>
            <div v-if="!player.isAi" class="slot-status" :class="{ online: player.online }">
              {{ player.online ? '已就绪' : '等待中...' }}
            </div>
            <div v-else class="slot-status">已就绪</div>
          </div>

          <!-- 空位 -->
          <div
            v-for="n in emptySlots"
            :key="'empty-' + n"
            class="player-slot empty-slot"
          >
            <div class="slot-avatar empty">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/></svg>
            </div>
            <div class="slot-name">等待加入</div>
          </div>
        </div>

        <div class="waiting-actions">
          <button class="leave-btn" @click="leaveRoom">离开房间</button>
          <button
            v-if="isHost"
            class="start-btn"
            :disabled="!canStart"
            @click="startGame"
            :loading="starting"
          >
            开始游戏
          </button>
          <div v-else class="wait-msg">等待房主开始游戏...</div>
        </div>
      </div>
    </template>

    <!-- ====== Playing State ====== -->
    <template v-else>
      <div class="game-board" :class="{ 'heiwu-mode': gameType === 2 }">
        <!-- 顶部工具栏 -->
        <div class="board-toolbar">
          <button class="back-btn" @click="confirmLeave">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
            退出
          </button>
          <span class="board-title">{{ gameTypeName }}</span>
          <span class="board-room-code">房间: {{ roomInfo.roomCode }}</span>
          <button class="rules-btn" @click="showRules = true">📖 玩法</button>
        </div>

        <!-- 桌面区域 -->
        <div class="table-area">
          <!-- 对家(4人局) -->
          <div class="seat seat-top" v-if="players.length >= 4 && seatPlayers.opposite">
            <div class="seat-info">
              <span class="landlord-crown" v-if="seatPlayers.opposite.seatIndex === landlordSeat">👑</span>
              <img :src="seatPlayers.opposite.avatar || defaultAvatar" class="seat-avatar" alt="">
              <span class="seat-name">{{ seatPlayers.opposite.nickname }}</span>
              <span class="card-count">剩余 {{ seatPlayers.opposite.cardCount }} 张</span>
            </div>
            <div class="seat-cards" v-if="showOtherCards">
              <span
                v-for="c in seatPlayers.opposite.cardCount"
                :key="'c-op-' + c"
                class="card-back"
              ></span>
            </div>
          </div>

          <!-- 左侧玩家(顺时针上一家) -->
          <div class="seat seat-left" v-if="players.length >= 3 && seatPlayers.prev">
            <div class="seat-info">
              <span class="landlord-crown" v-if="seatPlayers.prev.seatIndex === landlordSeat">👑</span>
              <img :src="seatPlayers.prev.avatar || defaultAvatar" class="seat-avatar" alt="">
              <span class="seat-name">{{ seatPlayers.prev.nickname }}</span>
              <span class="card-count">剩余 {{ seatPlayers.prev.cardCount }} 张</span>
            </div>
            <div class="seat-cards-vertical" v-if="showOtherCards">
              <span
                v-for="c in seatPlayers.prev.cardCount"
                :key="'c-prev-' + c"
                class="card-back-small"
              ></span>
            </div>
          </div>

          <!-- 右侧玩家(顺时针下一家) -->
          <div class="seat seat-right" v-if="players.length >= 2 && seatPlayers.next">
            <div class="seat-info">
              <span class="landlord-crown" v-if="seatPlayers.next.seatIndex === landlordSeat">👑</span>
              <img :src="seatPlayers.next.avatar || defaultAvatar" class="seat-avatar" alt="">
              <span class="seat-name">{{ seatPlayers.next.nickname }}</span>
              <span class="card-count">剩余 {{ seatPlayers.next.cardCount }} 张</span>
            </div>
            <div class="seat-cards-vertical" v-if="showOtherCards">
              <span
                v-for="c in seatPlayers.next.cardCount"
                :key="'c-next-' + c"
                class="card-back-small"
              ></span>
            </div>
          </div>

          <!-- 中央出牌区 -->
          <div class="play-area">
            <!-- 底牌(斗地主) -->
            <div class="landlord-cards-row" v-if="gameType === 1 && landlordCards.length > 0">
              <span class="landlord-label">底牌</span>
              <span
                v-for="(card, lci) in sortedLandlordCards"
                :key="'lc-' + lci"
                class="landlord-card"
                :class="cardSuitClass(card)"
              >{{ cardDisplay(card) }}</span>
            </div>
            <div class="turn-indicator" v-if="currentTurnSeat >= 0">
              <template v-if="isMyTurn">
                <span class="my-turn-label" :class="{ 'free-play': isFreePlay }">{{ isFreePlay ? '自由出牌' : '你的回合' }}</span>
              </template>
              <template v-else>
                <span>等待 {{ displayTurnName }} 出牌...</span>
              </template>
              <span class="timer" :class="{ urgent: timer <= 10 }">{{ timer }}s</span>
            </div>

            <!-- 上家出的牌 -->
            <div class="played-cards" v-if="lastPlayInfo">
              <div class="played-by">{{ realPlayerName(lastPlayInfo.seat, lastPlayInfo.playerName) }}</div>
              <div class="card-row">
                <span
                  v-for="(card, ci) in lastPlayInfo.cards"
                  :key="'played-' + ci"
                  class="played-card"
                  :class="cardSuitClass(card)"
                >
                  {{ cardDisplay(card) }}
                </span>
              </div>
              <div class="combo-label" v-if="lastPlayInfo.comboName">{{ lastPlayInfo.comboName }}</div>
            </div>
          </div>

          <!-- 底部(自己) -->
          <div class="seat seat-bottom">
            <div class="seat-info">
              <span class="landlord-crown" v-if="mySeatIndex === landlordSeat">👑</span>
              <img :src="myInfo?.avatar || defaultAvatar" class="seat-avatar" alt="">
              <span class="seat-name">{{ myInfo?.nickname || '我' }}</span>
              <span class="card-count">剩余 {{ myHand.length }} 张</span>
            </div>

            <!-- 手牌 -->
            <div class="my-hand">
              <div class="hand-cards" v-if="myHand.length > 0">
                <div
                  v-for="(card, ci) in sortedHand"
                  :key="'hand-' + ci"
                  class="hand-card"
                  :class="[cardSuitClass(card), { selected: selectedCards.includes(card) }]"
                  @click="toggleCard(card)"
                  :style="{ zIndex: ci, marginLeft: ci > 0 ? -20 + (selectedCards.includes(card) ? 0 : 0) + 'px' : '0' }"
                >
                  <div class="card-inner">
                    <span class="card-value">{{ cardValueDisplay(card) }}</span>
                    <span class="card-suit">{{ cardSuitSymbol(card) }}</span>
                  </div>
                </div>
              </div>
              <div class="hand-actions" v-if="isMyTurn">
                <button
                  class="action-btn play-btn"
                  :disabled="selectedCards.length === 0"
                  @click="playCards"
                >
                  出牌
                </button>
                <button class="action-btn pass-btn" @click="passTurn">
                  不要
                </button>
                <button class="action-btn hint-btn" @click="getHint">
                  提示
                </button>
              </div>
              <div class="hand-actions" v-else-if="gameStarted">
                <button class="action-btn pass-btn" disabled style="opacity:0.4">
                  等待出牌...
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 最近出牌记录 -->
        <div class="play-history" v-if="playHistory.length > 0">
          <div class="history-header">出牌记录</div>
          <div class="history-list">
            <div v-for="(h, hi) in playHistory" :key="'h-' + hi" class="history-item">
              <span class="history-player">{{ realPlayerName(h.seat, h.playerName) }}:</span>
              <span class="history-cards" v-if="h.cards && h.cards.length > 0">
                <span v-for="(c, ci) in h.cards" :key="'hc-' + ci" :class="cardSuitClass(c)">{{ cardDisplay(c) }}</span>
              </span>
              <span class="history-pass" v-else>不要</span>
            </div>
          </div>
        </div>

        <!-- 座位编号说明(挤黑五) -->
        <div class="seat-numbers" v-if="gameType === 2 && seatMap.length > 0">
          <div class="seat-num-info">
            <span v-for="(s, si) in seatMap" :key="'sm-' + si" class="seat-num-tag">
              {{ s }}号位: {{ seatPlayerName(s) }}
            </span>
          </div>
        </div>

        <!-- 游戏结束弹窗 -->
        <el-dialog v-model="showResult" title="游戏结束" width="380px" :close-on-click-modal="false">
          <div class="result-content">
            <div class="result-icon" :class="isWinner ? 'win' : 'lose'">
              {{ isWinner ? '🎉' : '😞' }}
            </div>
            <div class="result-text">{{ resultText }}</div>
            <div class="result-detail" v-if="resultDetail">
              <p v-for="(line, ri) in resultDetail" :key="'r-' + ri">{{ line }}</p>
            </div>
          </div>
          <template #footer>
            <el-button @click="leaveRoom">返回大厅</el-button>
            <el-button type="primary" @click="playAgain">再来一局</el-button>
          </template>
        </el-dialog>
      </div>
    </template>

    <!-- 玩法介绍弹窗（独立于等待/游戏状态，始终可访问） -->
    <el-dialog v-model="showRules" title="📖 玩法介绍" width="580px" top="5vh" :close-on-click-modal="true" class="rules-dialog">
      <template v-if="gameType === 2">
        <div class="rules-content">
          <section class="rule-section">
            <h3>玩法概述</h3>
            <p>挤黑五是地方特色玩法，融合了升级的组队机制、跑得快的出牌逻辑以及独特的隐藏身份要素。黑桃5（♠5）是游戏的核心牌，玩法围绕其展开。</p>
          </section>
          <section class="rule-section">
            <h3>基本规则</h3>
            <ul>
              <li><strong>玩家人数：</strong>4人或5人</li>
              <li><strong>组队：</strong>每局随机选择一名<strong>庄家</strong>，持有黑桃5的玩家为庄家的<strong>隐藏队友</strong>（可能即是庄家本人）</li>
              <li><strong>目标：</strong>庄家方两人尽快出完手牌</li>
            </ul>
          </section>
          <section class="rule-section">
            <h3>牌面大小</h3>
            <p><strong>4 &lt; 6 &lt; 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A &lt; 2 &lt; 3 &lt; 小王 &lt; 大王 &lt; 5</strong></p>
            <p class="rule-note">5是最大的牌，单张5只能被更大的5管住。花色优先级：♠ &gt; ♥ &gt; ♣ &gt; ♦</p>
          </section>
          <section class="rule-section">
            <h3>可出牌型</h3>
            <ul>
              <li><strong>单张：</strong>任意一张牌</li>
              <li><strong>对子：</strong>两张点数相同的牌</li>
              <li><strong>连对：</strong>至少3个连续点数的对子</li>
              <li><strong>炸弹：</strong>三张点数相同的牌，可管除王炸外的任何牌型</li>
              <li><strong>王炸：</strong>四张点数相同的牌（如两张小王+两张大王），最大的牌型</li>
            </ul>
          </section>
          <section class="rule-section">
            <h3>出牌流程</h3>
            <ul>
              <li><strong>首轮：</strong>持有黑桃5的玩家首先出牌（必须出包含♠5的牌型）</li>
              <li>按顺时针方向轮流出牌，可选择"不要"</li>
              <li>当其他玩家都"不要"时，上一手出牌者获得自由出牌权</li>
            </ul>
          </section>
          <section class="rule-section">
            <h3>胜负判定</h3>
            <ul>
              <li><strong>庄家方胜：</strong>庄家和黑桃5持有者都出完手牌，对方仍有玩家未出完</li>
              <li><strong>平局：</strong>黑桃5持有者第一个出完，但庄家最后一个出完</li>
              <li><strong>特殊情况：</strong>若庄家即是黑桃5持有者，则庄家需第一个出完才算赢</li>
            </ul>
          </section>
        </div>
      </template>
      <template v-else>
        <div class="rules-content">
          <section class="rule-section">
            <h3>基本规则</h3>
            <ul>
              <li><strong>玩家人数：</strong>3人（2真人 + 1人机）</li>
              <li><strong>目标：</strong>地主先出完则地主胜；任一农民先出完则农民方胜</li>
            </ul>
          </section>
          <section class="rule-section">
            <h3>发牌</h3>
            <ul>
              <li>标准54张扑克牌（含大小王），每人17张，留3张底牌</li>
              <li>采用<strong>随机地主</strong>模式，系统随机指定地主</li>
              <li>地主获得3张底牌，共20张手牌</li>
            </ul>
          </section>
          <section class="rule-section">
            <h3>牌型大小</h3>
            <ul>
              <li><strong>单张：</strong>3 &lt; 4 &lt; 5 &lt; ... &lt; K &lt; A &lt; 2 &lt; 小王 &lt; 大王</li>
              <li><strong>对子：</strong>两张点数相同的牌</li>
              <li><strong>三条：</strong>三张点数相同的牌</li>
              <li><strong>炸弹：</strong>四张点数相同的牌，可管除火箭外的任何牌型</li>
              <li><strong>火箭：</strong>小王+大王，最大的牌型</li>
            </ul>
          </section>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import gameApi from '@/api/game'
const route = useRoute()
const router = useRouter()

const userId = ref(Number(localStorage.getItem('userId')))
const defaultAvatar = 'https://picsum.photos/id/1005/200'

// Room state
const roomInfo = ref({})
const gameType = ref(0)
const gameStarted = ref(false)
const starting = ref(false)

// WebSocket
let ws = null
const wsReconnectTimer = ref(null)
const WS_URL = `ws://${window.location.hostname}:8078/ws`

// Game state
const players = ref([])
const myHand = ref([])
const selectedCards = ref([])
const currentTurnSeat = ref(-1)
const currentTurnName = ref('')
const isFreePlay = ref(false)
const timer = ref(30)
const lastPlayInfo = ref(null)
const showResult = ref(false)
const showRules = ref(false)
const isWinner = ref(false)
const resultText = ref('')
const resultDetail = ref(null)
const showOtherCards = ref(true)
const mySeatIndex = ref(-1)
const landlordSeat = ref(-1)
const landlordCards = ref([])

let timerInterval = null
let turnStartTime = 0
let lastInteractTime = 0

const gameTypeName = computed(() => {
  return gameType.value === 1 ? '斗地主' : gameType.value === 2 ? '挤黑五' : '游戏'
})

const isHost = computed(() => {
  return roomInfo.value.hostUserId === userId.value
})

const maxPlayers = computed(() => {
  return roomInfo.value.maxPlayers || 0
})

const emptySlots = computed(() => {
  const count = roomInfo.value.players?.length || 0
  return Math.max(0, maxPlayers.value - count)
})

const canStart = computed(() => {
  if (!isHost.value) return false
  const count = roomInfo.value.players?.length || 0
  if (gameType.value === 1) return count >= 1  // 斗地主至少1真人
  if (gameType.value === 2) return count >= 2  // 挤黑五至少2人
  return false
})

const isMyTurn = computed(() => {
  if (mySeatIndex.value < 0 || currentTurnSeat.value < 0) return false
  const seatPlayer = seatMap.value[currentTurnSeat.value]
  return seatPlayer === userId.value
})

// 当前回合玩家的实际昵称(优先用game_turn带的真实名，降级到players数组)
const displayTurnName = computed(() => {
  // 真实昵称(不包含"玩家""用户"等自动生成前缀)
  const isRealName = (name) => name && !name.startsWith('玩家') && !name.startsWith('用户')
  if (currentTurnName.value && isRealName(currentTurnName.value)) {
    return currentTurnName.value
  }
  const p = players.value.find(pl => pl.seatIndex === currentTurnSeat.value)
  if (p?.nickname && isRealName(p.nickname)) {
    return p.nickname
  }
  // 最后的fallback：显示座位号
  return currentTurnName.value || p?.nickname || `座位${currentTurnSeat.value + 1}`
})

const seatMap = computed(() => {
  // Map seat index to userId
  const map = []
  for (const p of players.value) {
    map[p.seatIndex] = p.userId
  }
  return map
})

const myInfo = computed(() => {
  return players.value.find(p => p.userId === userId.value) || null
})

const sortedHand = computed(() => {
  const cards = [...myHand.value]
  if (gameType.value === 2) {
    // 挤黑五排序：按挤黑五rank降序
    cards.sort((a, b) => (b.heiWuRank || b.rank || 0) - (a.heiWuRank || a.rank || 0))
  } else {
    // 斗地主排序：按rank降序
    cards.sort((a, b) => (b.rank || 0) - (a.rank || 0))
  }
  return cards
})

const sortedLandlordCards = computed(() => {
  const cards = [...landlordCards.value]
  if (gameType.value === 1) {
    cards.sort((a, b) => (b.rank || 0) - (a.rank || 0))
  }
  return cards
})

// 座位玩家计算：按顺时针相对当前玩家偏移
const seatPlayers = computed(() => {
  const total = players.value.length
  if (total === 0 || mySeatIndex.value < 0) return { next: null, prev: null, opposite: null }
  const find = (seatIdx) => players.value.find(p => p.seatIndex === seatIdx) || null
  return {
    next: find((mySeatIndex.value + 1) % total),     // 顺时针下一家(右侧)
    prev: find((mySeatIndex.value + total - 1) % total), // 顺时针上一家(左侧)
    opposite: total >= 4 ? find((mySeatIndex.value + 2) % total) : null // 对家
  }
})

// Card display helpers
const cardSuitSymbol = (card) => {
  if (!card) return ''
  if (card.suit === 'JOKER') return '🃏'
  if (card.suit === 'SPADE') return '♠'
  if (card.suit === 'HEART') return '♥'
  if (card.suit === 'CLUB') return '♣'
  if (card.suit === 'DIAMOND') return '♦'
  return ''
}

const cardValueDisplay = (card) => {
  if (!card) return ''
  if (card.suit === 'JOKER') return card.value === 1 ? '小' : '大'
  if (card.value === 0) return '3'
  if (card.value === 1) return '4'
  if (card.value === 2) return '5'
  if (card.value === 3) return '6'
  if (card.value === 4) return '7'
  if (card.value === 5) return '8'
  if (card.value === 6) return '9'
  if (card.value === 7) return '10'
  if (card.value === 8) return 'J'
  if (card.value === 9) return 'Q'
  if (card.value === 10) return 'K'
  if (card.value === 11) return 'A'
  if (card.value === 12) return '2'
  return String(card.display || card.value || '')
}

const cardDisplay = (card) => {
  if (!card) return ''
  if (card.suit === 'JOKER') return card.value === 2 ? '大王' : '小王'
  return cardSuitSymbol(card) + cardValueDisplay(card)
}

const cardSuitClass = (card) => {
  if (!card) return ''
  if (!card.suit || card.suit === 'JOKER') return 'card-joker'
  if (card.suit === 'HEART' || card.suit === 'DIAMOND') return 'card-red'
  return 'card-black'
}

const toggleCard = (card) => {
  if (!isMyTurn.value) return
  lastInteractTime = Date.now()  // 牌操作→重置空闲计时
  const idx = selectedCards.value.indexOf(card)
  if (idx >= 0) {
    selectedCards.value.splice(idx, 1)
  } else {
    selectedCards.value.push(card)
  }
}

const seatPlayerName = (seatIdx) => {
  const p = players.value.find(pl => pl.seatIndex === seatIdx)
  return p ? p.nickname : '空位'
}

// 从players数组找对应座位的真实昵称，避免显示"玩家X"等自动生成名
const realPlayerName = (seat, fallbackName) => {
  const isReal = (name) => name && !name.startsWith('玩家') && !name.startsWith('用户')
  if (isReal(fallbackName)) return fallbackName
  const p = players.value.find(pl => pl.seatIndex === seat)
  if (p?.nickname && isReal(p.nickname)) return p.nickname
  return fallbackName || (seat !== undefined ? `座位${seat + 1}` : '')
}

// ====== Sound Effects (Web Audio API) ======
let audioCtx = null
const getAudioCtx = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)()
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume()
  }
  return audioCtx
}

const playCardSound = () => {
  try {
    const ctx = getAudioCtx()
    // 短促"啪"声——方波脉冲 + 噪声
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(800, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.08)
    gain.gain.setValueAtTime(0.15, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.1)
  } catch (_) { /* 静默失败 */ }
}

const playPassSound = () => {
  try {
    const ctx = getAudioCtx()
    // 短促"呼"声——白噪声
    const bufferSize = ctx.sampleRate * 0.08
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.06
    }
    const source = ctx.createBufferSource()
    source.buffer = buffer
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.08, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08)
    source.connect(gain)
    gain.connect(ctx.destination)
    source.start(ctx.currentTime)
  } catch (_) { /* 静默失败 */ }
}

// ====== Play History ======
const playHistory = ref([])
const addPlayHistory = (entry) => {
  playHistory.value.push(entry)
  if (playHistory.value.length > 20) {
    playHistory.value.shift()
  }
}

// ====== Speech Synthesis (读出出了什么牌) ======
let speechSynth = null
const getSpeechSynth = () => {
  if (!speechSynth) {
    speechSynth = window.speechSynthesis || null
  }
  return speechSynth
}

// 只取牌的数值部分(不带花色)，用于"对子5"这样的朗读
const cardValueCN = (card) => {
  if (!card) return ''
  if (card.suit === 'JOKER') return card.value === 2 ? '大王' : '小王'
  const valMap = { 0:'3', 1:'4', 2:'5', 3:'6', 4:'7', 5:'8', 6:'9', 7:'10', 8:'J', 9:'Q', 10:'K', 11:'A', 12:'2' }
  return valMap[card.value] ?? String(card.value || '')
}

const cardNameCN = (card) => {
  if (!card) return ''
  if (card.suit === 'JOKER') return card.value === 2 ? '大王' : '小王'
  const suitMap = { SPADE: '黑桃', HEART: '红桃', CLUB: '梅花', DIAMOND: '方片',
                    spade: '黑桃', heart: '红桃', club: '梅花', diamond: '方片' }
  const s = suitMap[card.suit] || ''
  const v = cardValueCN(card)
  return s + v
}

const speakCards = (cards, isPass, comboName) => {
  try {
    const synth = getSpeechSynth()
    if (!synth) return
    if (isPass) {
      const u = new SpeechSynthesisUtterance('不要')
      u.lang = 'zh-CN'
      u.rate = 1.1
      synth.speak(u)
      return
    }
    if (!cards || cards.length === 0) return

    let text = ''
    if (comboName === '对子') {
      text = '对子' + cardValueCN(cards[0])
    } else if (comboName === '炸弹') {
      text = '炸弹' + cardValueCN(cards[0])
    } else if (comboName === '火箭') {
      text = '火箭'
    } else if (comboName === '三带一') {
      text = '三个' + cardValueCN(cards[0]) + '带' + cardValueCN(cards[3])
    } else if (comboName === '三带二') {
      text = '三个' + cardValueCN(cards[0]) + '带对子' + cardValueCN(cards[3])
    } else if (comboName === '顺子') {
      const sorted = [...cards].sort((a, b) => (a.rank || 0) - (b.rank || 0))
      text = '顺子' + cardValueCN(sorted[0]) + '到' + cardValueCN(sorted[sorted.length - 1])
    } else if (comboName === '连对') {
      const sorted = [...cards].sort((a, b) => (a.rank || 0) - (b.rank || 0))
      text = '连对' + sorted.filter((_, i) => i % 2 === 0).map(c => cardValueCN(c)).join('、')
    } else if (comboName === '飞机' || comboName === '飞机带单' || comboName === '飞机带双') {
      text = comboName
    } else {
      text = cards.map(c => cardNameCN(c)).join('、')
    }

    const u = new SpeechSynthesisUtterance(text)
    u.lang = 'zh-CN'
    u.rate = 0.9
    synth.speak(u)
  } catch (_) { /* ignore */ }
}

// ====== WebSocket ======
const connectWs = () => {
  if (ws && ws.readyState === WebSocket.OPEN) return
  try {
    ws = new WebSocket(WS_URL)
    ws.onopen = () => {
      // 发送连接认证
      ws.send(JSON.stringify({
        type: 'connect',
        userId: userId.value
      }))
      // 加入房间
      if (roomInfo.value.id) {
        ws.send(JSON.stringify({
          type: 'game_join_room',
          roomId: roomInfo.value.id,
          userId: userId.value
        }))
      }
    }
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        handleWsMessage(msg)
      } catch (e) {
        console.warn('WS message parse error:', e)
      }
    }
    ws.onclose = () => {
      // 自动重连
      wsReconnectTimer.value = setTimeout(connectWs, 3000)
    }
    ws.onerror = () => {
      ws?.close()
    }
  } catch (e) {
    wsReconnectTimer.value = setTimeout(connectWs, 3000)
  }
}

const handleWsMessage = (msg) => {
  switch (msg.type) {
    // ===== 后端发牌(斗地主/挤黑五) =====
    case 'game_dealt':
      gameStarted.value = true
      playHistory.value = []
      myHand.value = msg.hand || []
      players.value = (msg.players || []).map(p => ({
        ...p,
        cardCount: p.cardCount || 0
      }))
      mySeatIndex.value = msg.mySeat || 0
      landlordSeat.value = msg.landlordSeat !== undefined ? msg.landlordSeat : -1
      landlordCards.value = msg.landlordCards || []
      selectedCards.value = []
      lastPlayInfo.value = null
      break

    // ===== 回合通知 =====
    case 'game_turn':
      currentTurnSeat.value = msg.seat
      currentTurnName.value = msg.playerName || ''
      isFreePlay.value = msg.isFreePlay === true
      timer.value = msg.timeLimit || 20
      startTimer()
      selectedCards.value = []
      if (isFreePlay.value) {
        lastPlayInfo.value = null
      }
      break

    // ===== 出牌广播 =====
    case 'game_play': {
      const playedCards = msg.cards || []
      const comboLabel = msg.comboName || ''

      lastPlayInfo.value = {
        seat: msg.seat,
        playerName: msg.playerName || '',
        cards: playedCards,
        comboName: comboLabel
      }
      addPlayHistory({
        seat: msg.seat,
        playerName: msg.playerName || '',
        cards: playedCards,
        comboName: comboLabel
      })
      playCardSound()
      speakCards(playedCards, false, comboLabel)

      // 后端已确认出牌，从手牌移除(如果是自己的出牌)
      if (msg.seat === mySeatIndex.value && myHand.value.length > 0) {
        myHand.value = myHand.value.filter(h => !playedCards.some(p =>
          p.suit === h.suit && p.value === h.value && p.display === h.display
        ))
      }
      // 更新其他玩家手牌数
      if (msg.seat !== undefined) {
        const p = players.value.find(pl => pl.seatIndex === msg.seat)
        if (p && p.cardCount > 0) {
          p.cardCount = Math.max(0, p.cardCount - playedCards.length)
        }
      }
      break
    }

    // ===== 过牌广播 =====
    case 'game_pass': {
      lastPlayInfo.value = {
        seat: msg.seat,
        playerName: msg.playerName || '',
        cards: [],
        comboName: '不要'
      }
      addPlayHistory({
        seat: msg.seat,
        playerName: msg.playerName || '',
        cards: [],
        comboName: '不要'
      })
      playPassSound()
      speakCards([], true, null)
      break
    }

    // ===== 游戏事件 =====
    case 'game_event':
      if (msg.event === 'new_round') {
        lastPlayInfo.value = null
        currentTurnSeat.value = msg.seat || -1
      } else if (msg.event === 'player_join' || msg.event === 'player_left') {
        fetchRoomInfo()
      }
      break

    // ===== 游戏结束(后端通知，仅挤黑五用) =====
    case 'game_over':
      showResult.value = true
      isWinner.value = msg.winner === userId.value || (msg.winnerTeam && msg.winnerTeam.includes(userId.value))
      resultText.value = msg.resultText || (isWinner.value ? '你赢了！' : '你输了')
      resultDetail.value = msg.detail || null
      stopTimer()
      break

    case 'game_player_left': {
      const leftUserId = msg.userId
      players.value = players.value.filter(p => p.userId !== leftUserId)
      fetchRoomInfo()
      break
    }

    case 'game_room_update':
      if (msg.players) {
        roomInfo.value = { ...roomInfo.value, players: msg.players }
      }
      break

    case 'game_invite':
      if (msg.inviterName && msg.roomCode) {
        ElMessage.info({
          message: `${msg.inviterName} 邀请你加入棋牌对局，房间码: ${msg.roomCode}`,
          duration: 8000,
          showClose: true
        })
      }
      break
  }
}

const startTimer = () => {
  stopTimer()
  turnStartTime = Date.now()
  lastInteractTime = Date.now()
  timer.value = 30

  timerInterval = setInterval(() => {
    const now = Date.now()
    const idleMs = now - lastInteractTime
    const elapsedMs = now - turnStartTime

    // 30秒无操作 → 自动过牌
    if (idleMs >= 30000) {
      stopTimer()
      if (isMyTurn.value) passTurn()
      return
    }

    // 最长60秒 → 强制过牌
    if (elapsedMs >= 60000) {
      stopTimer()
      if (isMyTurn.value) passTurn()
      return
    }

    // 显示：取两个剩余时间中较小的
    const idleRemaining = Math.ceil((30000 - idleMs) / 1000)
    const totalRemaining = Math.ceil((60000 - elapsedMs) / 1000)
    timer.value = Math.max(1, Math.min(idleRemaining, totalRemaining))
  }, 1000)
}

const stopTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

const sendWsMessage = (data) => {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(data))
  }
}

// ====== Room Actions ======
const fetchRoomInfo = async () => {
  try {
    const roomId = route.params.roomId
    if (!roomId) return
    const res = await gameApi.getRoomInfo(roomId)
    if (res.data.success) {
      roomInfo.value = res.data.data
      gameType.value = res.data.data.gameType
      gameStarted.value = res.data.data.status === 1
      players.value = res.data.data.players || []
      // 找到自己的座位
      const me = players.value.find(p => p.userId === userId.value)
      if (me) {
        mySeatIndex.value = me.seatIndex
      }
      // 如果游戏已开始但还没有手牌(延迟加入/断线重连),从API获取
      if (gameStarted.value && myHand.value.length === 0) {
        fetchHand()
      }
    } else {
      ElMessage.error('获取房间信息失败')
      router.push('/game/lobby')
    }
  } catch (error) {
    ElMessage.error('获取房间信息失败')
    router.push('/game/lobby')
  }
}

const fetchHand = async () => {
  try {
    const res = await gameApi.getPlayerHand(roomInfo.value.id, userId.value)
    if (res.data.success) {
      myHand.value = res.data.data || []
      selectedCards.value = []
    }
  } catch (e) {
    console.warn('获取手牌失败', e)
  }
}

const copyRoomCode = () => {
  const code = roomInfo.value.roomCode
  if (code) {
    navigator.clipboard.writeText(code).then(() => {
      ElMessage.success('房间码已复制')
    }).catch(() => {
      ElMessage.success('房间码: ' + code)
    })
  }
}

const startGame = async () => {
  try {
    starting.value = true
    const res = await gameApi.startGame(userId.value, roomInfo.value.id)
    if (!res.data.success) {
      ElMessage.error(res.data.msg || '开始游戏失败')
    }
    // gameStarted将由WS game_dealt消息设置
  } catch (error) {
    ElMessage.error('开始游戏失败')
  } finally {
    starting.value = false
  }
}

const leaveRoom = async () => {
  try {
    await gameApi.leaveRoom(userId.value, roomInfo.value.id)
  } catch (e) {
    // ignore
  }
  stopTimer()
  disconnectWs()
  router.push('/game/lobby')
}

const confirmLeave = async () => {
  try {
    await ElMessageBox.confirm('确定要退出当前游戏吗？', '退出游戏', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    leaveRoom()
  } catch (e) {
    // cancelled
  }
}

const playAgain = () => {
  showResult.value = false
  // 房主重新开始
  if (isHost.value) {
    startGame()
  }
}

// ====== Game Actions ======
const playCards = () => {
  if (selectedCards.value.length === 0 || !isMyTurn.value) return

  const indices = selectedCards.value
    .map(c => myHand.value.indexOf(c))
    .filter(i => i >= 0)
  if (indices.length === 0) return

  sendWsMessage({
    type: 'game_play',
    roomId: roomInfo.value.id,
    seatIndex: mySeatIndex.value,
    cardIndices: indices
  })
  stopTimer()
  selectedCards.value = []
}

const passTurn = () => {
  if (!isMyTurn.value) return

  sendWsMessage({
    type: 'game_pass',
    roomId: roomInfo.value.id,
    seatIndex: mySeatIndex.value
  })
  stopTimer()
  selectedCards.value = []
}

const getHint = () => {
  // Simple hint: just select the smallest card
  if (sortedHand.value.length > 0) {
    selectedCards.value = [sortedHand.value[sortedHand.value.length - 1]]
  }
}

const disconnectWs = () => {
  if (wsReconnectTimer.value) {
    clearTimeout(wsReconnectTimer.value)
    wsReconnectTimer.value = null
  }
  if (ws) {
    ws.onclose = null
    ws.close()
    ws = null
  }
}

// ====== Lifecycle ======
let waitingPollTimer = null

onMounted(() => {
  if (!route.params.roomId) {
    router.push('/game/lobby')
    return
  }
  fetchRoomInfo().then(() => {
    connectWs()
    // 等待房间中轮询，防止WS消息丢失导致卡在等待页
    startWaitingPoll()
  })
})

onUnmounted(() => {
  stopTimer()
  disconnectWs()
  stopWaitingPoll()
})

const startWaitingPoll = () => {
  stopWaitingPoll()
  waitingPollTimer = setInterval(() => {
    // 只有等待状态才轮询
    if (!gameStarted.value && roomInfo.value.id) {
      fetchRoomInfo()
    } else {
      stopWaitingPoll()
    }
  }, 3000)
}

const stopWaitingPoll = () => {
  if (waitingPollTimer) {
    clearInterval(waitingPollTimer)
    waitingPollTimer = null
  }
}
</script>

<style scoped>
.game-room {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 12px 8px;
  min-height: calc(100vh - 100px);
}

/* ── Waiting Room ── */
.waiting-room {
  max-width: 560px;
  margin: 40px auto;
  text-align: center;
}

.waiting-header {
  margin-bottom: 32px;
}

.waiting-header h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--app-text-primary);
  margin: 0 0 20px;
}

.room-code-label {
  font-size: 0.85rem;
  color: var(--app-text-muted);
  display: block;
  margin-bottom: 8px;
}

.room-code {
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 12px;
  color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
  padding: 12px 24px;
  border-radius: var(--app-radius-lg);
  display: inline-block;
  font-family: 'Courier New', monospace;
}

.waiting-hint {
  font-size: 0.85rem;
  color: var(--app-text-muted);
  margin: 16px 0 12px;
}

.copy-btn {
  padding: 8px 24px;
  border: 1px solid var(--app-color-primary-light);
  border-radius: var(--app-radius-full);
  background: transparent;
  color: var(--app-color-primary);
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
}
.copy-btn:hover {
  background: var(--app-color-primary-soft);
}

.player-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
  margin: 32px 0;
}

.player-slot {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  padding: 20px 12px;
  border: 1px solid var(--app-border-subtle);
  transition: all 0.3s;
  position: relative;
}
.player-slot.is-host {
  border-color: var(--app-color-primary-light);
}
.player-slot.is-self {
  background: var(--app-color-primary-soft);
}

.slot-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  margin: 0 auto 10px;
  position: relative;
  overflow: hidden;
}
.slot-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.slot-avatar.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--app-bg-soft);
  color: var(--app-text-muted);
}
.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #95a5a6;
  border: 2px solid var(--app-bg-elevated);
}
.online-indicator.online {
  background: #2ecc71;
}

.slot-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--app-text-primary);
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.slot-badges {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.host-badge, .ai-badge, .self-badge {
  font-size: 0.7rem;
  padding: 1px 8px;
  border-radius: var(--app-radius-full);
}
.host-badge {
  background: #fff3cd;
  color: #856404;
}
.ai-badge {
  background: var(--app-bg-soft);
  color: var(--app-text-muted);
}
.self-badge {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}

.slot-status {
  font-size: 0.8rem;
  color: var(--app-text-muted);
}
.slot-status.online {
  color: #2ecc71;
}

.empty-slot {
  border-style: dashed;
  opacity: 0.5;
}

.waiting-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
}

.leave-btn {
  padding: 10px 28px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-full);
  background: transparent;
  color: var(--app-text-secondary);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}
.leave-btn:hover {
  border-color: #e74c3c;
  color: #e74c3c;
}

.start-btn {
  padding: 10px 32px;
  border: none;
  border-radius: var(--app-radius-full);
  background: var(--app-gradient-primary);
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.start-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(255,126,126,0.3);
}
.start-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wait-msg {
  color: var(--app-text-muted);
  font-size: 0.9rem;
}

/* ── Game Board ── */
.game-board {
  position: relative;
  min-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.board-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
  margin-bottom: 12px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-full);
  background: var(--app-bg-elevated);
  color: var(--app-text-secondary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}
.back-btn:hover {
  border-color: #e74c3c;
  color: #e74c3c;
}

.rules-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-full);
  background: var(--app-bg-elevated);
  color: var(--app-text-secondary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  white-space: nowrap;
}
.rules-btn:hover {
  border-color: var(--app-color-primary-light);
  color: var(--app-color-primary);
}

.board-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--app-text-primary);
  flex: 1;
}

.board-room-code {
  font-size: 0.85rem;
  color: var(--app-text-muted);
}

.table-area {
  flex: 1;
  background: linear-gradient(135deg, #1a5c34 0%, #2d8a4e 100%);
  border-radius: var(--app-radius-xl);
  padding: 16px 8px;
  display: grid;
  grid-template-areas:
    '. top-center .'
    'left-seat play-area right-seat'
    '. bottom-seat .';
  grid-template-columns: minmax(80px, 140px) 1fr minmax(80px, 140px);
  grid-template-rows: auto 1fr auto;
  gap: 8px;
  min-height: 480px;
  position: relative;
  border: 4px solid #3a7a4a;
  box-shadow: inset 0 0 60px rgba(0,0,0,0.2);
}

.landlord-crown {
  font-size: 1rem;
  margin-right: 2px;
}

.landlord-cards-row {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0,0,0,0.25);
  padding: 4px 12px;
  border-radius: var(--app-radius-full);
  margin-bottom: 4px;
}

.landlord-label {
  font-size: 0.7rem;
  color: rgba(255,255,255,0.5);
  margin-right: 4px;
}

.landlord-card {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 50px;
  border-radius: 4px;
  background: white;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}

/* Seats */
.seat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.seat-top {
  grid-area: top-center;
  justify-self: center;
}

.seat-left {
  grid-area: left-seat;
  justify-self: center;
}

.seat-right {
  grid-area: right-seat;
  justify-self: center;
}

.seat-bottom {
  grid-area: bottom-seat;
}

.seat-info {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0,0,0,0.3);
  padding: 6px 14px;
  border-radius: var(--app-radius-full);
  color: white;
}

.seat-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255,255,255,0.2);
}

.seat-name {
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
}

.card-count {
  font-size: 0.75rem;
  opacity: 0.7;
  white-space: nowrap;
}

.seat-cards {
  display: flex;
  gap: 2px;
  justify-content: center;
}

.card-back {
  width: 24px;
  height: 36px;
  background: linear-gradient(135deg, #2c3e50, #3498db);
  border-radius: 3px;
  border: 1px solid rgba(255,255,255,0.1);
}

.seat-cards-vertical {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
}

.card-back-small {
  width: 20px;
  height: 28px;
  background: linear-gradient(135deg, #2c3e50, #3498db);
  border-radius: 3px;
  border: 1px solid rgba(255,255,255,0.1);
  margin: -14px 0 0;
}
.card-back-small:first-child {
  margin-top: 0;
}

/* ── Play Area ── */
.play-area {
  grid-area: play-area;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 160px;
}

.turn-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(0,0,0,0.4);
  padding: 6px 20px;
  border-radius: var(--app-radius-full);
  color: white;
  font-size: 0.9rem;
}

.my-turn-label {
  color: #ffd700;
  font-weight: 700;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.timer {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  background: rgba(0,0,0,0.3);
  padding: 2px 12px;
  border-radius: var(--app-radius-full);
  min-width: 40px;
  text-align: center;
}
.timer.urgent {
  color: #ff4444;
  animation: pulse 0.5s infinite;
}

.played-cards {
  text-align: center;
}

.played-by {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.6);
  margin-bottom: 6px;
}

.card-row {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.played-card {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 68px;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.combo-label {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.5);
  margin-top: 4px;
}

/* Hand cards */
.my-hand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.hand-cards {
  display: flex;
  justify-content: center;
  padding: 0 20px;
  min-height: 90px;
}

.hand-card {
  width: 52px;
  height: 76px;
  border-radius: 6px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  margin-left: -24px;
  flex-shrink: 0;
  position: relative;
}
.hand-card:first-child {
  margin-left: 0;
}
.hand-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}
.hand-card.selected {
  transform: translateY(-20px);
  box-shadow: 0 6px 20px rgba(255,215,0,0.3);
}

.card-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  line-height: 1;
}

.card-value {
  font-size: 0.95rem;
  font-weight: 800;
}

.card-suit {
  font-size: 1rem;
}

.card-red .card-value,
.card-red .card-suit {
  color: #e74c3c;
}

.card-black .card-value,
.card-black .card-suit {
  color: #2c3e50;
}

.card-joker .card-value,
.card-joker .card-suit {
  color: #8e44ad;
}

/* ── Play History ── */
.play-history {
  background: rgba(0,0,0,0.3);
  border-radius: var(--app-radius-md);
  padding: 8px 12px;
  color: rgba(255,255,255,0.8);
  font-size: 0.8rem;
  max-height: 120px;
  overflow-y: auto;
  margin-top: 4px;
  grid-column: 1 / -1;
}
.history-header {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.4);
  margin-bottom: 4px;
}
.history-item {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 2px 0;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.history-player {
  color: rgba(255,255,255,0.6);
  flex-shrink: 0;
}
.history-cards span {
  display: inline-block;
  margin-right: 2px;
}
.history-pass {
  color: rgba(255,255,255,0.3);
  font-style: italic;
}

/* Result dialog */
.result-content {
  text-align: center;
  padding: 20px 0;
}

.result-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.result-text {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--app-text-primary);
  margin-bottom: 12px;
}

.result-detail {
  font-size: 0.85rem;
  color: var(--app-text-secondary);
  line-height: 1.6;
}
.result-detail p {
  margin: 4px 0;
}

/* ── Rules Dialog ── */
.rules-dialog :deep(.el-dialog__body) {
  padding: 12px 20px 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.rules-content {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rule-section {
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  padding: 14px 16px;
  border: 1px solid var(--app-border-subtle);
}

.rule-section h3 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--app-text-primary);
  margin: 0 0 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--app-border-light);
}

.rule-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.rule-section ul li {
  position: relative;
  padding: 4px 0 4px 14px;
  color: var(--app-text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;
}
.rule-section ul li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: var(--app-color-primary);
}

.rule-section p {
  font-size: 0.85rem;
  color: var(--app-text-secondary);
  line-height: 1.6;
  margin: 0;
}

.rule-note {
  margin-top: 6px !important;
  font-size: 0.8rem !important;
  color: var(--app-text-muted) !important;
}

/* Hand actions */
.hand-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 8px 24px;
  border: none;
  border-radius: var(--app-radius-full);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.play-btn {
  background: var(--app-color-primary);
  color: white;
}
.play-btn:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(255,126,126,0.3);
}

.pass-btn {
  background: rgba(255,255,255,0.2);
  color: white;
}
.pass-btn:hover {
  background: rgba(255,255,255,0.3);
}

.hint-btn {
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.7);
  font-weight: 400;
}
.hint-btn:hover {
  background: rgba(255,255,255,0.2);
}

/* Seat numbers */
.seat-numbers {
  margin-top: 8px;
  padding: 8px 12px;
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-subtle);
}

.seat-num-info {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: var(--app-text-muted);
}

.seat-num-tag {
  padding: 2px 8px;
  border-radius: var(--app-radius-full);
  background: var(--app-bg-soft);
}

@media (max-width: 768px) {
  .table-area {
    grid-template-columns: 70px 1fr 70px;
    padding: 8px 4px;
    min-height: 360px;
    gap: 4px;
  }

  .hand-card {
    width: 40px;
    height: 58px;
    margin-left: -20px;
    font-size: 0.8rem;
  }
  .hand-card .card-value {
    font-size: 0.75rem;
  }
  .hand-card .card-suit {
    font-size: 0.8rem;
  }

  .seat-avatar {
    width: 22px;
    height: 22px;
  }
  .seat-name {
    font-size: 0.7rem;
    max-width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .card-count {
    font-size: 0.6rem;
  }

  .played-card {
    width: 36px;
    height: 50px;
    font-size: 0.7rem;
  }

  .seat-cards-vertical .card-back-small {
    width: 14px;
    height: 20px;
    margin: -8px 0 0;
  }
  .card-back {
    width: 16px;
    height: 24px;
  }

  .seat-info {
    padding: 4px 8px;
    gap: 4px;
  }
}
</style>
